import { api } from '../api.js';
import { hydrateImages } from './docTools.js';

// Document blocks (Insert block): technical table, picture + details,
// picture row, two columns — the Website content blocks adapted to
// documents. In the text a block is <div class="dblock" data-block="{json}">;
// the server checks the JSON (docBlocks.js) and draws the PDF from it. Here
// it is drawn for the editor and the previews (same look as the site's
// technical tables and picture sections).

export const BLOCK_KINDS = ['tech', 'media', 'gallery', 'cols'];

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

export function newBlock(kind) {
  if (kind === 'tech') return { kind, title: '', headers: ['', '', ''], rows: [['', '', ''], ['', '', '']], note: '', striped: true, accent: true, borders: 'light', unitCol: -1 };
  if (kind === 'media') return { kind, img: null, side: 'left', size: 40, caption: '', title: '', html: '', specs: [] };
  if (kind === 'gallery') return { kind, items: [{ img: null, caption: '' }, { img: null, caption: '' }, { img: null, caption: '' }] };
  return { kind: 'cols', left: '', right: '' };
}

export const readBlock = (el) => { try { return JSON.parse(el.dataset.block || 'null'); } catch { return null; } };

const pic = (id, cls = '') => (id ? `<img data-img="${Number(id)}" alt="" class="${cls}">` : `<span class="db-pic-empty ${cls}" aria-hidden="true">🖼</span>`);

// Inside of a block (editor, previews).
export function blockInner(b) {
  if (!b) return '';
  if (b.kind === 'tech') {
    const unit = Number.isInteger(b.unitCol) ? b.unitCol : -1;
    const head = `<thead><tr>${(b.headers || []).map((h, k) => `<th class="${k === unit ? 'is-unit' : ''}">${esc(h)}</th>`).join('')}</tr></thead>`;
    const body = `<tbody>${(b.rows || []).map((r) => `<tr>${(b.headers || []).map((_, k) => (k === 0 ? `<th>${esc(r[k])}</th>` : `<td class="${k === unit ? 'is-unit' : ''}">${esc(r[k])}</td>`)).join('')}</tr>`).join('')}</tbody>`;
    const cls = ['db-table', b.striped === false ? '' : 'is-striped', b.accent === false ? '' : 'is-accent', b.borders === 'none' ? 'no-borders' : ''].filter(Boolean).join(' ');
    return `${b.title ? `<p class="db-title">${esc(b.title)}</p>` : ''}<div class="db-table-wrap"><table class="${cls}">${head}${body}</table></div>${b.note ? `<p class="db-note">${esc(b.note)}</p>` : ''}`;
  }
  if (b.kind === 'media') {
    const specs = (b.specs || []).length ? `<dl class="db-specs">${b.specs.map(([l, v]) => `<div><dt>${esc(l)}</dt><dd>${esc(v)}</dd></div>`).join('')}</dl>` : '';
    return `<div class="db-media is-${b.side || 'left'} is-${b.size || 40}"><figure class="db-media-pic">${pic(b.img)}${b.caption ? `<figcaption>${esc(b.caption)}</figcaption>` : ''}</figure>`
      + `<div class="db-media-text">${b.title ? `<p class="db-media-title">${esc(b.title)}</p>` : ''}<div class="db-rich">${b.html || ''}</div>${specs}</div></div>`;
  }
  if (b.kind === 'gallery') {
    return `<div class="db-gallery is-${(b.items || []).length}">${(b.items || []).map((it) => `<figure>${pic(it.img)}${it.caption ? `<figcaption>${esc(it.caption)}</figcaption>` : ''}</figure>`).join('')}</div>`;
  }
  return `<div class="db-cols"><div class="db-rich">${b.left || ''}</div><div class="db-rich">${b.right || ''}</div></div>`;
}

// The block element put in the editor.
export function blockElement(b) {
  const el = document.createElement('div');
  el.className = 'dblock';
  el.dataset.block = JSON.stringify(b);
  el.contentEditable = 'false';
  el.innerHTML = blockInner(b);
  return el;
}

// Letterhead accent colour (technical table headers), once per page load.
let accent = null;
function loadAccent() {
  if (accent) return accent;
  accent = api('/workspace/doc-letterhead').then((r) => r.letterhead?.accent || null).catch(() => null);
  return accent;
}

// Draws the blocks of a box (editor or preview) from their JSON.
export function hydrateBlocks(root, { editable = false } = {}) {
  if (!root) return;
  const list = root.querySelectorAll('div.dblock[data-block], div[data-block]');
  for (const el of list) {
    el.classList.add('dblock');
    if (editable) el.contentEditable = 'false';
    el.innerHTML = blockInner(readBlock(el));
  }
  if (list.length) hydrateImages(root);
  // The editor gets the colour even before its first block.
  if (list.length || editable) loadAccent().then((c) => { if (c && /^#[0-9a-f]{6}$/i.test(c)) root.style.setProperty('--doc-accent', c); });
}
